import {
  Button,
  Spinner,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import type { SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import {
  bundleIcon,
  ArrowLeft24Filled,
  ArrowLeft24Regular,
  Dismiss16Regular,
  Info16Filled,
  Warning16Filled,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useEvalStats } from '../../../hooks/useEvalStats/useEvalStats';
import type { LocalSigsEntry, MetadataCache } from '../../../types/sigs';
import {
  getGroundingSections,
  getGroundingTabLabel,
} from '../../../utils/groundingConfig';
import { DebugTrajectoryTimeline } from '../DebugTrajectoryTimeline/DebugTrajectoryTimeline.js';
import { EnvironmentFooter } from '../EnvironmentFooter/EnvironmentFooter';
import { EvalMetricsGrid } from '../EvalMetricsGrid/EvalMetricsGrid';
import { GroundingDataSourcePanel } from '../GroundingDataSourcePanel/GroundingDataSourcePanel';
import { GroundingPanel } from '../GroundingPanel/GroundingPanel';
import { InteractionSummary } from '../InteractionSummary/InteractionSummary';
import { RawJsonViewer } from '../RawJsonViewer/RawJsonViewer';
import { ShareSignalDialog } from '../ShareSignalDialog/ShareSignalDialog';
import { SkillExecutionTable } from '../SkillExecutionTable/SkillExecutionTable';
import { VariantsPanel } from '../VariantsPanel/VariantsPanel';

import type { DebugDetailViewProps } from './DebugDetailView.interface';
import { useStyles } from './DebugDetailView.style';

const BackIcon = bundleIcon(ArrowLeft24Filled, ArrowLeft24Regular) as React.FC;

type DetailTab =
  | 'overview'
  | 'execution'
  | 'grounding'
  | 'evals'
  | 'config'
  | 'debugTrajectory'
  | 'raw'
  | 'related'
  | 'notes';

// ---------------------------------------------------------------------------
// Related signals tab
// ---------------------------------------------------------------------------

interface RelatedTabProps {
  currentSignalId: string;
  currentCv: string;
  currentItemId: string;
  currentAppName: string;
  onSelectCv: (cv: string) => void;
}

interface RelatedEntry {
  /** Storage key (itemId) — used for navigation */
  storageKey: string;
  /** Human-readable actual CV */
  displayCv: string;
  startTime?: string;
  promptPreview?: string;
}

function RelatedTab({
  currentSignalId,
  currentCv,
  currentItemId,
  currentAppName,
  onSelectCv,
}: RelatedTabProps) {
  const styles = useStyles();
  const [entries, setEntries] = useState<RelatedEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        if (currentAppName === 'CopilotCowork' && currentItemId) {
          try {
            const recentRes = await fetch('/api/sigs/recent?days=7&refresh=1');
            if (!recentRes.ok) {
              console.error(
                `Failed to refresh Cowork signal metadata: ${recentRes.status}`,
              );
            }
          } catch (error) {
            console.error('Failed to refresh Cowork signal metadata:', error);
          }
        }

        const [localRes, metaRes] = await Promise.all([
          fetch('/api/sigs/local'),
          fetch('/api/sigs/metadata'),
        ]);
        const localData: LocalSigsEntry[] = localRes.ok
          ? ((await localRes.json()) as LocalSigsEntry[])
          : [];
        const metaData: MetadataCache = metaRes.ok
          ? ((await metaRes.json()) as MetadataCache)
          : {};

        const metaEntries = Object.values(metaData);
        if (currentAppName === 'CopilotCowork' && currentItemId) {
          const coworkEntries = metaEntries
            .filter(
              (entry) =>
                entry.appName === 'CopilotCowork' &&
                entry.itemId === currentItemId &&
                entry.signalId !== currentSignalId,
            )
            .map((entry) => ({
              storageKey: entry.signalId,
              displayCv: entry.cv,
              startTime: entry.startTime,
              promptPreview: entry.promptPreview,
            }));
          coworkEntries.sort(
            (a, b) =>
              new Date(a.startTime ?? 0).getTime() -
              new Date(b.startTime ?? 0).getTime(),
          );
          setEntries(coworkEntries);
          return;
        }

        // Determine base itemId from metadata (preferred) or local store
        const currentMetaEntry = metaEntries.find(
          (e) => e.signalId === currentSignalId || e.cv === currentCv,
        );
        const currentLocalEntry = localData.find(
          (e) => e.signalCv === currentCv,
        );
        const baseItemId = currentMetaEntry
          ? currentMetaEntry.itemId.split('.')[0]
          : currentLocalEntry
            ? currentLocalEntry.cv.split('.')[0]
            : null;
        if (!baseItemId) return;

        // Related entries from metadata (same base itemId, different cv)
        const relatedMeta = metaEntries.filter(
          (e) => e.cv !== currentCv && e.itemId.split('.')[0] === baseItemId,
        );

        // Related entries from local store (same base storage key, different entry)
        const baseStorageKey =
          currentLocalEntry?.cv.split('.')[0] ?? baseItemId;
        const relatedLocal = localData.filter(
          (e) =>
            e.cv !== currentLocalEntry?.cv &&
            e.cv.split('.')[0] === baseStorageKey,
        );

        // Merge: prefer local entries (they have promptPreview), supplement with metadata-only
        const localSignalCvSet = new Set(
          relatedLocal.map((e) => e.signalCv ?? e.cv),
        );
        const metaOnlyEntries = relatedMeta.filter(
          (e) => !localSignalCvSet.has(e.cv),
        );

        const combined: RelatedEntry[] = [
          ...relatedLocal.map((e) => ({
            storageKey: e.cv,
            displayCv: e.signalCv ?? e.cv,
            startTime: e.startTime,
            promptPreview: e.promptPreview,
          })),
          ...metaOnlyEntries.map((e) => ({
            storageKey: e.itemId,
            displayCv: e.cv,
            startTime: e.startTime,
            promptPreview: undefined,
          })),
        ];

        combined.sort(
          (a, b) =>
            new Date(b.startTime ?? 0).getTime() -
            new Date(a.startTime ?? 0).getTime(),
        );
        setEntries(combined);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentAppName, currentCv, currentItemId, currentSignalId]);

  if (loading) {
    return (
      <div className={styles.relatedSpinner}>
        <Spinner size="small" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={styles.relatedEmptyState}>
        <Text>No related signals found.</Text>
      </div>
    );
  }

  return (
    <div className={styles.relatedList}>
      {entries.map((entry) => (
        <div
          key={entry.storageKey}
          className={styles.relatedEntry}
          onClick={() => onSelectCv(entry.storageKey)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectCv(entry.storageKey);
            }
          }}
        >
          <Text className={styles.relatedCv}>{entry.displayCv}</Text>
          {entry.startTime && (
            <Text className={styles.relatedTime}>
              {new Date(entry.startTime).toLocaleString()}
            </Text>
          )}
          {entry.promptPreview && (
            <Text className={styles.relatedPrompt}>{entry.promptPreview}</Text>
          )}
        </div>
      ))}
    </div>
  );
}

const DETAIL_TABS: DetailTab[] = [
  'overview',
  'execution',
  'grounding',
  'evals',
  'config',
  'debugTrajectory',
  'raw',
  'related',
  'notes',
];

function isDetailTab(s: string): s is DetailTab {
  return (DETAIL_TABS as string[]).includes(s);
}

export function DebugDetailView({
  data,
  origin,
  onBack,
  onSelectCv,
  shareDescription: shareDescriptionProp,
}: DebugDetailViewProps) {
  const styles = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [truncatedBannerDismissed, setTruncatedBannerDismissed] =
    useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const requestedTab = searchParams.get('tab') ?? '';
  const tabParam = requestedTab === 'jsonl' ? 'debugTrajectory' : requestedTab;
  const groundingSections = getGroundingSections(data.agentType, data);
  // Any agent with a parsed trajectory gets the tab, not just Teams Agent.
  const trajectoryPayload =
    data.debugTrajectory && data.debugTrajectory.entries.length > 0
      ? data.debugTrajectory
      : undefined;
  const isRequestedTabAvailable =
    isDetailTab(tabParam) &&
    (tabParam !== 'grounding' ||
      groundingSections.length > 0 ||
      !!data.groundingDataPayload) &&
    (tabParam !== 'debugTrajectory' || !!trajectoryPayload) &&
    (tabParam !== 'related' || !!data.cv) &&
    (tabParam !== 'notes' || !!shareDescriptionProp);
  const activeTab: DetailTab = isRequestedTabAvailable ? tabParam : 'overview';

  useEffect(() => {
    toolbarRef.current?.focus();
  }, []);

  const backLabel =
    origin === 'from-list' ? 'Back to List' : 'All Interactions';

  const evalNames = useMemo(
    () => data.evaluationMetrics.map((m) => m.evaluationName),
    [data.evaluationMetrics],
  );
  const { stats: evalStats } = useEvalStats({
    agentFriendlyName: data.friendlyAgentName,
    evalNames,
  });

  const handleTabChange = useCallback(
    (_e: SelectTabEvent, d: SelectTabData) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', d.value as string);
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <div className={styles.root}>
      {/* Toolbar */}
      <div className={styles.toolbar} ref={toolbarRef} tabIndex={-1}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacingHorizontalM,
          }}
        >
          <Button appearance="subtle" icon={<BackIcon />} onClick={onBack}>
            {backLabel}
          </Button>
        </div>

        <div className={styles.toolbarRight}>
          <ShareSignalDialog
            signalId={data.id}
            itemId={data.itemId}
            agentType={data.friendlyAgentName}
            userPrompt={data.userPrompt}
            rawSignal={data.rawSignal}
            disabled={!data.cv}
          />
        </div>
      </div>

      {/* Banners */}
      {!data.hasCompressedData && (
        <div className={styles.infoBanner}>
          <Info16Filled className={styles.infoBannerIcon} />
          <span>
            Signal found but CompressedData is missing. Showing available
            metadata only.
          </span>
        </div>
      )}

      {data.wasTruncated && !truncatedBannerDismissed && (
        <div className={styles.warningBanner}>
          <Warning16Filled className={styles.warningBannerIcon} />
          <span>
            This signal&apos;s ReferencedDocuments was truncated at the source
            due to SIGS payload size limits. Some data (conversation history,
            skill responses) may be incomplete.
          </span>
          <button
            type="button"
            className={styles.warningBannerDismiss}
            onClick={() => setTruncatedBannerDismissed(true)}
            aria-label="Dismiss warning"
          >
            <Dismiss16Regular />
          </button>
        </div>
      )}

      {/* Detail Tabs */}
      <TabList
        className={styles.tabBar}
        selectedValue={activeTab}
        onTabSelect={handleTabChange}
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="execution">Tools</Tab>
        {(groundingSections.length > 0 || !!data.groundingDataPayload) && (
          <Tab value="grounding">{getGroundingTabLabel(data.agentType)}</Tab>
        )}
        <Tab value="evals">Evals</Tab>
        <Tab value="config">Config</Tab>
        {trajectoryPayload && <Tab value="debugTrajectory">Trajectory</Tab>}
        <Tab value="raw">Raw</Tab>
        {data.cv && <Tab value="related">Related</Tab>}
        {shareDescriptionProp && <Tab value="notes">Notes</Tab>}
      </TabList>

      <div className={styles.tabContent}>
        {activeTab === 'overview' && <InteractionSummary data={data} />}

        {activeTab === 'execution' && <SkillExecutionTable data={data} />}

        {activeTab === 'grounding' && (
          <>
            {groundingSections.map((section) => (
              <GroundingPanel
                key={section.key}
                section={section}
                rawData={data[section.dataField]}
              />
            ))}
            {data.groundingDataPayload && (
              <GroundingDataSourcePanel payload={data.groundingDataPayload} />
            )}
          </>
        )}

        {activeTab === 'evals' && (
          <EvalMetricsGrid data={data} evalStats={evalStats} />
        )}

        {activeTab === 'config' && (
          <>
            <VariantsPanel data={data} />
            <EnvironmentFooter data={data} />
          </>
        )}

        {activeTab === 'debugTrajectory' && trajectoryPayload && (
          <DebugTrajectoryTimeline payload={trajectoryPayload} />
        )}

        {activeTab === 'raw' && (
          <RawJsonViewer
            data={data.rawSignal}
            filename={`sigs-debug-${data.cv ? data.cv.slice(0, 8) : 'unknown'}.json`}
          />
        )}

        {activeTab === 'related' && data.cv && (
          <RelatedTab
            currentSignalId={data.id}
            currentCv={data.cv}
            currentItemId={data.itemId}
            currentAppName={data.appName}
            onSelectCv={onSelectCv}
          />
        )}

        {activeTab === 'notes' && shareDescriptionProp && (
          <div style={{ padding: tokens.spacingVerticalM }}>
            <Text
              size={400}
              weight="semibold"
              block
              style={{ marginBottom: tokens.spacingVerticalS }}
            >
              Share Description
            </Text>
            <Text block style={{ whiteSpace: 'pre-wrap' }}>
              {shareDescriptionProp}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
